<template>
  <div class="flex items-center gap-2 h-full">
    <div
      class="relative flex flex-col gap-3 shrink-0 p-4 w-130.5 h-full rounded-6xl glass-frosted overflow-hidden"
    >
      <div class="flex item-center gap-2.5">
        <!-- 预览放大时，背景遮罩 -->
        <Transition
          enter-active-class="animate-in fade-in duration-200"
          leave-active-class="animate-out fade-out duration-200"
        >
          <div v-show="expanded" class="absolute inset-0 bg-mask20 z-50" />
        </Transition>

        <div class="relative p-3 flex-1 h-38 rounded-4xl bg-white">
          <textarea
            v-model="textContent"
            :maxlength="charLimit"
            class="w-full h-full resize-none pb-5 text-text-body-default text-m-medium"
            :placeholder="$t('pageAndPlayer.pages.text.inputPlaceholder')"
          />
          <span
            class="absolute right-3 bottom-2 text-s-medium text-text-body-default pointer-events-none select-none"
          >
            <span :class="textLength > 0 ? 'text-brand-blue1' : ''">{{ textLength }}</span
            >/{{ charLimit }}
          </span>
        </div>
        <div class="relative flex-1 p-3 h-38 rounded-4xl">
          <div
            :class="[
              'absolute right-0 top-0 rounded-4xl overflow-hidden cursor-default',
              'transition-[width,height] duration-200 ease-in-out',
              expanded ? 'z-100 w-106 h-60' : 'h-38 w-full',
            ]"
            :style="{ backgroundColor: bgColorHex, containerType: 'size' }"
          >
            <div class="w-full h-full flex items-center justify-center overflow-hidden min-w-0">
              <p
                v-if="textContent"
                class="w-full min-w-0 max-w-full overflow-hidden text-center leading-none"
                :style="{
                  color: textColorHex,
                  fontSize: `${previewFontSizeCqh}cqh`,
                }"
              >
                {{ previewText }}
              </p>
            </div>
            <div class="absolute top-2 right-2" @click.stop="expanded = !expanded">
              <Button color="blue1" size="xs">
                {{
                  expanded
                    ? $t('pageAndPlayer.pages.text.minimize')
                    : $t('pageAndPlayer.pages.text.preview')
                }}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <p class="text-black-white text-m-medium">{{ $t('pageAndPlayer.pages.text.fontSize') }}</p>
        <div class="flex-1 flex items-center gap-3">
          <Button
            color="normal"
            class="flex-1"
            :active="currentSize === 'small'"
            @click="currentSize = 'small'"
          >
            {{ $t('pageAndPlayer.pages.text.fontSmall') }}
          </Button>
          <Button
            color="normal"
            class="flex-1"
            :active="currentSize === 'medium'"
            @click="currentSize = 'medium'"
          >
            {{ $t('pageAndPlayer.pages.text.fontNormal') }}
          </Button>
          <Button
            color="normal"
            class="flex-1"
            :active="currentSize === 'large'"
            @click="currentSize = 'large'"
          >
            {{ $t('pageAndPlayer.pages.text.fontLarge') }}
          </Button>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <div class="flex-1 flex items-center gap-2">
          <p class="text-black-white text-m-medium">
            {{ $t('pageAndPlayer.pages.text.fontColor') }}
          </p>
          <div
            class="flex-1 h-6 rounded-md border-2 cursor-pointer"
            :class="currentColorPick === 'text' ? 'border-brand-blue1' : 'border-white'"
            :style="{ backgroundColor: textColorHex }"
            @click="currentColorPick = 'text'"
          />
        </div>
        <div class="flex-1 flex items-center gap-2">
          <p class="text-black-white text-m-medium">{{ $t('pageAndPlayer.pages.text.bgColor') }}</p>
          <div
            class="flex-1 h-6.5 rounded-md border-2 cursor-pointer"
            :class="currentColorPick === 'bg' ? 'border-brand-blue1' : 'border-white'"
            :style="{ backgroundColor: bgColorHex }"
            @click="currentColorPick = 'bg'"
          />
        </div>
      </div>
    </div>
    <div class="flex-1 p-4 h-full rounded-6xl glass-frosted">
      <ColorPicker v-model="pickerColor" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import type { RGB } from '@rapoo/device-sdk'
  import ColorPicker from './ColorPicker.vue'
  import { Button } from '@rapoo/element'
  import { rgbToHex } from '@/utils/color'
  import {
    getPreviewFontSizeCqh,
    normalizeScreenTextContent,
    SCREEN_TEXT_CHAR_LIMITS,
    createDefaultScreenTextStyle,
    type ScreenTextSize,
    type ScreenTextStyle,
  } from '../../utils/screenText'

  type ColorPickTarget = 'text' | 'bg'

  const props = defineProps<{
    resetKey?: number
  }>()

  const emit = defineEmits<{
    'update:modelValue': [ScreenTextStyle]
  }>()

  const defaultStyle = createDefaultScreenTextStyle()
  const textContent = ref(defaultStyle.content)
  const textColor = ref({ ...defaultStyle.textColor })
  const bgColor = ref({ ...defaultStyle.bgColor })
  const currentSize = ref<ScreenTextSize>(defaultStyle.size)
  const currentColorPick = ref<ColorPickTarget>('text')
  const expanded = ref(false)

  const charLimit = computed(() => SCREEN_TEXT_CHAR_LIMITS[currentSize.value])
  const textLength = computed(() => textContent.value.length)

  watch(currentSize, (size) => {
    const limit = SCREEN_TEXT_CHAR_LIMITS[size]
    if (textContent.value.length > limit) {
      textContent.value = textContent.value.slice(0, limit)
    }
  })

  watch([textContent, textColor, bgColor, currentSize], syncModel, { deep: true, immediate: true })

  watch(
    () => props.resetKey,
    (key) => {
      if (key) reset()
    },
  )

  function reset() {
    const style = createDefaultScreenTextStyle()
    textContent.value = style.content
    textColor.value = { ...style.textColor }
    bgColor.value = { ...style.bgColor }
    currentSize.value = style.size
    currentColorPick.value = 'text'
    expanded.value = false
  }

  const previewFontSizeCqh = computed(() => getPreviewFontSizeCqh(currentSize.value))

  const previewText = computed(() => normalizeScreenTextContent(textContent.value))

  const textColorHex = computed(() => rgbToHex(textColor.value))
  const bgColorHex = computed(() => rgbToHex(bgColor.value))

  const pickerColor = computed<RGB>({
    get() {
      return currentColorPick.value === 'text' ? textColor.value : bgColor.value
    },
    set(val) {
      if (currentColorPick.value === 'text') {
        textColor.value = val
      } else {
        bgColor.value = val
      }
    },
  })

  function syncModel() {
    emit('update:modelValue', {
      content: textContent.value,
      textColor: { ...textColor.value },
      bgColor: { ...bgColor.value },
      size: currentSize.value,
    })
  }
</script>

<style scoped></style>
